'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import type { MoodleCourse } from '@/types/moodle.types';
import { cleanHtml } from '@/lib/utils';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { LMS_EVENTS } from '@/lib/analytics';

interface CourseCardProps {
  course: MoodleCourse;
  index: number;
}

function StarRating({ rating = 0 }: { rating?: number }) {
  if (!rating) return null;

  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-3 h-3 ${star <= Math.round(rating) ? 'text-amber-400' : 'text-brand-border'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </span>
  );
}

/**
 * Sub-component for Course Badges (Bestseller, New, etc.)
 */
const CourseBadges = ({ course }: { course: MoodleCourse }) => {
  if (!course.isBestseller && !course.isNew) return null;

  return (
    <div className="absolute top-3 left-3 flex gap-2">
      {course.isBestseller && (
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-400 text-amber-950 shadow-sm">
          BESTSELLER
        </span>
      )}
      {course.isNew && (
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-brand-success text-white shadow-sm">
          NEW
        </span>
      )}
    </div>
  );
};

/**
 * Sub-component for Course Meta Info (Duration, Lessons)
 */
const CourseMeta = ({ course }: { course: MoodleCourse }) => {
  const hasDuration = Boolean(course?.duration && course.duration.length > 0);
  const hasLessons = Boolean(course?.lessonCount && course.lessonCount > 0);

  if (!hasDuration && !hasLessons) return null;

  return (
    <div className="flex items-center justify-between text-[10px] font-bold text-brand-text-muted pt-2 border-t border-brand-border">
      {hasDuration && (
        <div className="flex items-center gap-1.5">
          <svg
            className="w-3.5 h-3.5 text-brand-border-dark"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {course.duration}
        </div>
      )}
      {hasLessons && (
        <div className="flex items-center gap-1.5">
          <svg
            className="w-3.5 h-3.5 text-brand-border-dark"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414A1 1 0 0 1 19 9.414V19a2 2 0 0 1-2 2z"
            />
          </svg>
          {course.lessonCount} Sessions
        </div>
      )}
    </div>
  );
};

/**
 * Sub-component for Progress Bar
 */
const CourseProgressBar = ({ progress }: { progress?: number }) => {
  if (typeof progress !== 'number' || progress <= 0) return null;
  const roundedProgress = Math.round(progress);

  return (
    <div className="space-y-1.5 pt-2">
      <div className="flex items-center justify-between text-[10px] font-bold">
        <span className="text-brand-text-muted uppercase tracking-wider">
          {roundedProgress === 100 ? 'Course Completed' : 'Course Progress'}
        </span>
        <span className="text-brand-text-dark">{roundedProgress}%</span>
      </div>
      <div className="h-1.5 w-full bg-brand-bg rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-1000 ${roundedProgress === 100 ? 'bg-brand-success' : 'bg-brand-primary'}`}
          style={{ width: `${roundedProgress}%` }}
        />
      </div>
    </div>
  );
};

export default function CourseCard({ course, index }: CourseCardProps) {
  const { track } = useAnalytics();
  const progress = course.progress || 0;
  const showRating = Boolean(course?.rating && course.rating > 0);
  const showEnrollments = Boolean(course?.enrolledCount && course.enrolledCount > 0);
  const showCreatorDetails = false;

  return (
    <Link
      href={`/courses/${course.id}`}
      className="group card flex flex-col overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border border-brand-border rounded-3xl h-full"
      style={{ animationDelay: `${index * 50}ms` }}
      onClick={() =>
        track(LMS_EVENTS.COURSE_CARD_CLICKED, {
          courseId: course.id,
          courseName: course.title,
          position: index,
          progress,
        })
      }
    >
      {/* Cover image */}
      <div className="relative overflow-hidden aspect-video bg-brand-bg">
        <Image
          src={
            course.courseImage ||
            'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=800'
          }
          alt={course.title}
          fill
          className="object-cover group-hover:scale-110 transition-transform duration-700"
          unoptimized
        />
        <CourseBadges course={course} />
      </div>

      {/* Card body */}
      <div className="flex flex-col flex-1 p-5 space-y-4">
        {/* Creator */}
        {showCreatorDetails && course.creator && (
          <div className="flex items-center gap-2.5">
            <div className="relative w-6 h-6 rounded-full overflow-hidden ring-1 ring-brand-border">
              <Image
                src={
                  course.creatorAvatar ||
                  `https://api.dicebear.com/7.x/avataaars/svg?seed=${course.creator}`
                }
                alt={course.creator || 'Instructor'}
                fill
                className="object-cover"
                unoptimized
              />
            </div>
            <span className="text-xs text-brand-text-muted font-semibold">
              {course.creator || 'Instructor'}
            </span>
          </div>
        )}

        {/* Title & Desc */}
        <div className="space-y-1.5 flex-1">
          <h3 className="text-base font-bold text-brand-text-dark leading-tight transition-colors line-clamp-2">
            {course.title}
          </h3>
          <div
            className="text-xs text-brand-text-muted leading-relaxed line-clamp-2"
            dangerouslySetInnerHTML={{ __html: cleanHtml(course.description) }}
          />
        </div>

        <CourseMeta course={course} />
        <CourseProgressBar progress={course.progress} />

        {/* Progress/Rating Footer */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex flex-col gap-0.5">
            {showRating && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-black text-brand-text-dark">
                  {course?.rating?.toFixed(1)}
                </span>
                <StarRating rating={course.rating} />
                {/* {Boolean(course.ratingCount && course.ratingCount > 0) && (
                  <span className="text-[10px] text-brand-text-muted font-bold">
                    ({course.ratingCount})
                  </span>
                )} */}
              </div>
            )}
            {showEnrollments && (
              <span className="text-[9px] text-brand-text-muted font-bold uppercase tracking-wider">
                {course.enrolledCount} enrollments
              </span>
            )}
          </div>

          <div
            className={`flex items-center text-white uppercase text-[10px] font-black px-4 py-2 rounded-xl transition-colors shadow-lg ${
              progress === 100
                ? 'bg-brand-success hover:bg-brand-success/90 shadow-brand-success/10'
                : 'bg-brand-primary hover:bg-brand-primary-dark shadow-brand-primary/10'
            }`}
          >
            {progress === 100 ? 'Review' : progress > 0 ? 'Resume' : 'Start'}
            <svg
              className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
}
